import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SharedService } from '../Services/shared.service';
import { APIConfigMapping, APP_BUILDER_PERMISSIONS, HTTPAPIConfig, PERMISSIONS_ACTIONS, VlDataService, VlHttpService } from 'shared-library';
import { WORKFLOW_STAGE_ACTION_TYPES } from '../../constants/proj.cnst';
import { Subject, takeUntil } from 'rxjs';

export interface PageDetails {
  pageId: string;
  pageName: string;
  pageType: string;
}

@Component({
  selector: 'lib-stage',
  templateUrl: './stage.component.html',
  styleUrls: ['./stage.component.scss']
})

export class StageComponent implements OnInit,OnDestroy{

  queryParams: {
    [key: string]: string;
  } = {};

  stageId!: string;
  stageDetails: any;
  isEdit: boolean = false;
  workFlow!: string;
  entity!: string;
  stageSpinner:boolean = false;

  workFlowStageForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    page: new FormControl('', [Validators.required]),
    stageType: new FormControl('mandatory', [Validators.required]),
    accessrole: new FormControl('', [Validators.required]),
    approvalType: new FormControl('reporting', [Validators.required]),
    role: new FormControl('')
  });

  pageListDropdown: PageDetails[] = [];
  isRoleAccess: boolean = false;
  roleList: any[] = [];

  stageValidationForm: FormGroup = new FormGroup({
    approval: new FormControl(false, [Validators.required]),
    folds: new FormControl(false, [Validators.required]),
    nApproval: new FormControl(1),
    nFolds: new FormControl(1)
  })
  isApproval: boolean = false;
  isFolds: boolean = false;

  stageActionForm: FormGroup = new FormGroup({
    actionType: new FormControl(''),
    nextStage: new FormControl('')
  })
  actionType: string[] = this.dataService.deepClone(WORKFLOW_STAGE_ACTION_TYPES);
  stageList: any = [];
  isNextStage: boolean = false;
  isConditionalNextStage: boolean = false;
  destroy$:Subject<void> = new Subject<void>();
  pages!:any;

  appPermissions = APP_BUILDER_PERMISSIONS;
  permissionsActions = PERMISSIONS_ACTIONS;


  constructor(
    private toastrService: ToastrService,
    private sharedService: SharedService,
    private httpService: VlHttpService,
    private dataService: VlDataService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.subscribeStageEvents();
    this.subscribeRouterEvents();
    this.subscribeFormEvents();
  }

  subscribeFormEvents() {
    this.propertiesChange();
    this.validationChange();
    this.actionChange();
  }

  subscribeStageEvents() {
    this.sharedService.onMobileScreenDropSubjectdata$.pipe(takeUntil(this.destroy$)).subscribe(async (response: any) => {
      this.workFlowStageForm.reset();
      this.stageValidationForm.reset();
      this.stageActionForm.reset();
      this.isEdit = response['isEdit'];
      this.workFlow = response['workflow'];
      this.entity = response['entity'];
      if(response['isEdit']) {
        this.stageId = response['stage'];
        this.stageSpinner = true;
        this.stageDetails = await this.fectchStageDetails();
        this.setStageDetailsForm();
        this.stageSpinner = false;
      }
    });
  }

  propertiesChange() {
    this.workFlowStageForm.get('approvalType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {     
      this.isRoleAccess = value === 'role';
      if(this.isRoleAccess) {
        this.workFlowStageForm.get('role')?.addValidators([Validators.required]);
      }
    });
  }

  validationChange() {
    this.stageValidationForm.get('approval')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.isApproval = value;
      this.stageValidationForm.get('nApproval')?.addValidators([Validators.required]);
    })
    this.stageValidationForm.get('folds')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.isFolds = value;
      this.stageValidationForm.get('nFolds')?.addValidators([Validators.required]);
    })
  }

  actionChange() {
    this.stageActionForm.get('actionType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(async (value: string) => {
      if(value === 'Next Stage') {
        this.isConditionalNextStage = false;
        this.isNextStage = true;
        this.stageList = await this.fectchStageDetails('workflow');
        this.stageList = this.stageList.filter((stage:any) =>  stage.stage_id !== this.stageDetails.stage_id);
      }else if(value === 'Conditional Next Stage') {
        this.isConditionalNextStage = true;
      }
    })
  }

  subscribeRouterEvents() {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
      this.queryParams = {};
      for (const param in params) {
        if (params.hasOwnProperty(param)) {
          this.queryParams[param] = params[param];
        }
      }
      this.initMethods();
    });
  }

  async initMethods() {
    await this.fetchPages();
   this.pageListDropdown = await this.fetchEntity(this.entity);
    await this.fetchRoles();
  }

  async fectchStageDetails(type: string = 'stage') {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['STAGE_FETCH']);

    apiConfig.postData = {
      fetch_type: type,
      fetch_id: type == 'stage'? this.stageId: this.workFlow
    }

    return await new Promise((resolve, reject) => {
      this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          resolve(response.response);
        },
        error: (error: any) => {
          if(error?.status == 403) {
            this.toastrService.error('You do not have permission to access this resource.');
          }
          console.log(error, 'Error in Fetching Stage Details');
          reject(error);
        }
      })
    })
  }

  async fetchPages() {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_PAGES']);

    apiConfig.postData = {
      application_id: this.queryParams['id']
    }
    const defaultPages = ['Splash Screen', 'Login', 'Preview'];
    const defaultPageTypes = ['dataListView','PreviewPage'];
    this.pages = await new Promise((resolve, reject) => {
      this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: PageDetails[]) => {          
          const filteredPages = response.filter((value: PageDetails, index: number) => !defaultPages.includes(value.pageName) && !defaultPageTypes.includes(value?.pageType));          
          resolve(filteredPages);
        },
        complete: () => {},
        error: (error: any) => {
          if(error?.status == 403) {
            this.toastrService.error('You do not have permission to access this resource.');
          }
          console.log(error, 'Error in Fetch Pages API');
          reject(error);
        }
      })
    })
  }

  async fetchEntity(entity_id: string) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_ENTITIES_OR_ATTRIBUTES']);
    apiConfig.postData = {
      application_id: this.queryParams['id'],
      entity_id: entity_id,
      project_id: null,
      parent_entity_id: null
    }
    const returnData: any = await new Promise((resolve: any, reject) => {
      this.httpService.placeAPICall(apiConfig).subscribe({
        next: (response: any) => {
          let dropdownpages = this.pages.filter((page:any) => response.data.page_ids.includes(page.pageId));
          resolve(dropdownpages);
        },
        complete: () => {},
        error: (error: any) => {
          if(error?.status == 403) {
            this.toastrService.error('You do not have permission to access this resource.');
          }
          console.log(error, 'Error in Fetch entity API');
          reject(error);
        } 
      });
    });  
    
    return returnData;
  }

  async fetchRoles() {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['FETCH_ROLES']);

    apiConfig.postData = {
      user_id: JSON.parse(localStorage.getItem('userInfo') ?? 'null')?.userId
    }

    this.roleList = await new Promise((resolve, reject) => {
      this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          // this.sharedService.getApplicationData()
          let applicationId = this.sharedService.getApplicationDataEntire().application_id;
          let roles = response.response.filter((role:any) => role.applicationId === applicationId);
          resolve(response.result? roles : []);
        },
        complete: () => {},
        error: (error: any) => {
          if(error?.status == 403) {
            this.toastrService.error('You do not have permission to access this resource.');
          }
          console.log(error, 'Error in Fetching Roles');
          reject(error);
        }
      })
    })
  }

  setStageDetailsForm() {
    const stageConfig = this.stageDetails['stage_config'];
    const validationDetails = stageConfig?.['validationDetails'];
    const actionDetails = stageConfig?.['action'];
    this.workFlowStageForm.get('name')?.setValue(this.stageDetails['stage_name']);
    this.workFlowStageForm.get('stageType')?.setValue(this.stageDetails['stage_type']);
    this.workFlowStageForm.get('page')?.setValue(stageConfig?.['page']);
    this.workFlowStageForm.get('accessrole')?.setValue(stageConfig?.['accessRole']);
    this.workFlowStageForm.get('approvalType')?.setValue(stageConfig?.['approval']['type']);
    if(stageConfig?.['approval']['type'] === 'role') {
      this.workFlowStageForm.get('role')?.setValue(stageConfig?.['approval']['role']);
    }

    this.stageValidationForm.get('approval')?.setValue(validationDetails?.['approval']);
    this.stageValidationForm.get('nApproval')?.setValue(validationDetails?.['approvalLevel']);
    this.stageValidationForm.get('folds')?.setValue(validationDetails?.['itteration']);
    this.stageValidationForm.get('nFolds')?.setValue(validationDetails?.['itterationLevel']);

    this.stageActionForm.get('actionType')?.setValue(actionDetails?.['type']);
    this.stageActionForm.get('nextStage')?.setValue(actionDetails?.['nextStage']);
  }

  saveStage() {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['STAGE_CREATE_UPDATE']);
    const stageConfiguaration = this.generatePageConfigurations();
    apiConfig.postData = {
      workflow_id: this.workFlow,
      stage_name: this.workFlowStageForm.get('name')?.value,
      stage_description: '',
      stage_type: this.workFlowStageForm.get('stageType')?.value,
      stage_config: stageConfiguaration,
      userId: JSON.parse(localStorage.getItem('userInfo') ?? 'null').userId
    }

    if(this.isEdit) {
      apiConfig.postData['stage_id'] = this.stageId;
    }

    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.toastrService.success('Stage Updated Successfully');
      },
      complete: () => {
        this.updateWorkFlowList();
      },
      error: (error: any) => {
        if(error?.status == 403) {
          this.toastrService.error('You do not have permission to access this resource.');
        }
        console.log(error, 'Error in Creating Stage');
        this.toastrService.error('Error in Creating Stage:', this.workFlowStageForm.get('name')?.value);
      }
    })
  }

  generatePageConfigurations() {
    const returnData: any = {};
    const approvalDetails: any = {};
    const validationDetails: any = {};
    const actionDetails: any = {};
    approvalDetails['type'] = this.workFlowStageForm.get('approvalType')?.value;
    if(this.isRoleAccess) {
      approvalDetails['role'] = this.workFlowStageForm.get('role')?.value;
    }
    returnData['page'] = this.workFlowStageForm.get('page')?.value;
    returnData['approval'] = approvalDetails;
    returnData['accessRole'] = this.workFlowStageForm.get('accessrole')?.value;
    validationDetails['approval'] = this.stageValidationForm.get('approval')?.value;
    validationDetails['approvalLevel'] = this.stageValidationForm.get('nApproval')?.value;
    validationDetails['itteration'] = this.stageValidationForm.get('folds')?.value;
    validationDetails['itterationLevel'] = this.stageValidationForm.get('nFolds')?.value;
    returnData['validationDetails'] = validationDetails;
    actionDetails['type'] = this.stageActionForm.get('actionType')?.value;
    actionDetails['nextStage'] = this.stageActionForm.get('nextStage')?.value;
    returnData['action'] = actionDetails;
    return returnData;
  }

  updateWorkFlowList() {
    this.sharedService.setWorkFlowSubject({status: true, entity: this.entity});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
